import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099NECInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-NEC instructions for Tax year 2025"
          description="Get clear IRS Form 1099-NEC instructions for Tax Year 2025. Follow step-by-step guidance to complete and report nonemployee compensation with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1099-nec-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099 NEC</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-nec-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-NEC at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-NEC
                        </strong>{' '}
                        is used to report nonemployee compensation paid to
                        independent contractors, freelancers, consultants,
                        attorneys, and similar workers.
                      </span>
                    </li>

                    <li>
                      <span>
                        Businesses must file and furnish Form 1099-NEC by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          January 31<sup>st</sup>
                        </strong>{' '}
                        of following the tax year.
                      </span>
                    </li>

                    <li>
                      <span>
                        Businesses generally must file it if they paid{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $600 or more{' '}
                        </strong>
                        in a tax year, and they should collect{' '}
                        <strong style={{ fontWeight: 600 }}>
                          <Link
                            href="/resources/essential-forms/form-w-9-instructions/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form W-9
                          </Link>
                        </strong>{' '}
                        from contractors to obtain accurate taxpayer
                        information.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          IRS penalties
                        </strong>{' '}
                        for late filing, ranging from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return,{' '}
                        </strong>
                        depending on how late the form is filed or whether there
                        is intentional disregard.
                      </span>
                    </li>

                    <li>
                      <span>
                        This article covers{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Line-by-line filing instructions,{' '}
                        </strong>
                        including payer details, recipient details, nonemployee
                        compensation in Box 1, direct sales in Box 2, golden
                        parachute payments in Box 3, backup withholding in Box
                        4, and optional state reporting in Boxes 5-7.
                      </span>
                    </li>
                    <li>
                      <span>
                        If you need additional time to{' '}
                        <Link
                          href="/information-returns/efile-irs-form-1099-nec/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          file Form 1099-NEC
                        </Link>{' '}
                        , you can request an extension by submitting
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 8809
                        </strong>{' '}
                        on paper. Mailed the completed form 1099-NEC to the IRS
                        by the original filing due date.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-NEC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 1099-NEC is a tax return that businesses and
                  individuals must file each year to report payments made to
                  independent contractors or freelancers. This documents the
                  income they received from you so it can be properly reported
                  on their tax return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1099-NEC replaces the previous{' '}
                  <Link
                    href="/resources/information-returns/form-1099-misc-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1099-MISC form
                  </Link>{' '}
                  for reporting non-employee compensation.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 1099-NEC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you or your business paid $600 or more in non-employee
                  compensation in a tax year, you are required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-NEC
                  </Link>{' '}
                  with the IRS and provide a copy to the contractor. This
                  includes payments to contractors, freelancers, consultants,
                  attorneys, etc.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payments reported in Form 1099-NEC:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Over $600 was paid for services by a non-employee: Report
                      in Box 1.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Legal fees of over $600 were paid to an attorney: Also,
                      report in Box 1.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Sales of products over $5,000 made on commission for
                      resale: Use Form 1099-NEC or 1099-MISC.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Withheld federal income tax under backup withholding
                      rules: Report in Box 4.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payments exempt from Form 1099-NEC:
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  The IRS states these payments should not be reported on Form
                  1099-NEC.
                </Typography>

                <List sx={{ mb: 4 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments to corporations (except medical services)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments for merchandise, telegrams, freight, storage,
                      etc.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Rent paid to real estate agents or property managers
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Wages, bonuses, and prizes paid to employees (use Form
                      W-2)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Military differential pay (use Form W-2)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Business travel allowances (may go on Form W-2)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Insurance premiums (use Form W-2 or 1099-R)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments to tax-exempt organizations
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Payments to government entities</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Homeowner assistance funds</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Certain disability or survivor benefits
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Compensation for wrongful incarceration
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-NEC due date?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <Link
                    href="/resources/information-returns/form-1099-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    due date for filing Form 1099-NEC
                  </Link>{' '}
                  is January 31st of the year following the payment. For
                  example, if you made payments in 2025, you must file Form
                  1099-NEC by <b>February 02, 2026</b>.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Avoid the last-minute 1099-NEC deadline stress!
                      </Typography>
                      <Typography variant="body2">
                        E-file now for easy IRS compliance and peace of mind.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099necinstructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  The due date to provide copies of Form 1099-NEC to recipients
                  is also February 02. By providing recipient copies within the
                  deadline, you can ensure the recipient has the tax
                  documentation they need to accurately report the income on
                  their tax return.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-NEC penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you fail to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-NEC
                  </Link>{' '}
                  for your contractors within the deadline, the IRS will impose
                  a penalty for each missed return. Below is the breakdown of
                  Form 1099-NEC penalties:
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Don&apos;t risk penalties!
                    </Typography>
                    <Typography variant="body2">
                      Be proactive! E-file your 1099-NEC forms ahead of the
                      deadline.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099necinstructions`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-NEC Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-NEC.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-payer-img.webp" />
                  <source srcSet="/assets/images/nec-payer-img.png" />
                  <img
                    src="/assets/images/nec-payer-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-payer details"
                    width={590}
                    height={233}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter your or your business name
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter your complete address
                  </li>
                  <li>
                    Enter your TIN (SSN if you&apos;re an individual; EIN if
                    you&apos;re a business)
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-recipient-img.webp" />
                  <source srcSet="/assets/images/nec-recipient-img.png" />
                  <img
                    src="/assets/images/nec-recipient-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-recipient details"
                    width={590}
                    height={319}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Recipient&apos;s TIN: Enter the TIN (SSN for an individual;
                    EIN for a business)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the recipient&apos;s name
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the recipient&apos;s complete address, including
                    street address, city or town, state or province, country,
                    and ZIP or foreign postal code
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Account number: If you have multiple accounts set up for a
                    single recipient to whom you will need to issue more than
                    one Form 1099-NEC, you must include the applicable account
                    number for that recipient on each form.
                  </li>
                  <li>
                    2nd TIN not: The IRS requires you to verify that you have
                    the correct Taxpayer Identification Number (TIN) for
                    individuals and businesses for whom you file 1099 forms.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Nonemployee compensation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-box-1-img.webp" />
                  <source srcSet="/assets/images/nec-box-1-img.png" />
                  <img
                    src="/assets/images/nec-box-1-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-box -1-nonemployee compensation"
                    width={485}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the nonemployee compensation of $600 or more paid in the
                  tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Payer made direct sales totaling $5,000 or more
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-box-2-img.webp" />
                  <source srcSet="/assets/images/nec-box-2-img.png" />
                  <img
                    src="/assets/images/nec-box-2-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-box-2-payer made direct sales totaling $5,000 or more"
                    width={485}
                    height={59}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if your business sold $5,000 or more worth of
                  consumer goods directly to an individual who will then resell
                  the items. Do not enter a dollar amount — just check the box.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Excess golden parachute payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-box-3-img.webp" />
                  <source srcSet="/assets/images/nec-box-3-img.png" />
                  <img
                    src="/assets/images/nec-box-3-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-box-3-excess parachute payments"
                    width={432}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any excess golden parachute payments. These payments
                  represent the portion that exceeds the “base amount,” which is
                  calculated as the individual’s average taxable compensation
                  over the previous five tax year
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-box-4-img.webp" />
                  <source srcSet="/assets/images/nec-box-4-img.png" />
                  <img
                    src="/assets/images/nec-box-4-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-box-4-federal income tax withheld"
                    width={485}
                    height={59}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any federal income tax that was withheld from payments
                  made to a contractor or freelancer.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This is known as backup withholding. It applies when a payee
                  has not provided you with their TIN.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 5–7: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/nec-box-5-img.webp" />
                  <source srcSet="/assets/images/nec-box-5-img.png" />
                  <img
                    src="/assets/images/nec-box-5-img.png"
                    loading="lazy"
                    alt="Form 1099-nec-box-5-7-state information"
                    width={675}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Boxes 5-7 are optional state-level reporting boxes. You do not
                  need to complete these for the IRS.
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Box 5: Report any state income tax withheld from the payment
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box 6: Enter the 2-letter state abbreviation and
                    payer&apos;s ID number for that state
                  </li>
                  <li>
                    Box 7: Enter the amount of compensation associated with that
                    state
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 1099-NEC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1099-NEC can be filed electronically or by postal mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1099-NEC electronic filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-NEC
                  </Link>{' '}
                  is recommended by the IRS as the process is quick and simple.
                  Additionally, you will receive notifications as soon as the
                  IRS processes your return.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  With TaxZerone, you can complete the e-filing process in just
                  a few minutes and clicks. Simply fill out the form, review and
                  transmit it to the IRS, and share the recipient copy.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form 1099-NEC, here are the steps
                  you need to follow:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print Form 1099-NEC from the IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Fill out the recipient&apos;s name, address, TIN, and
                    compensation amount in the required boxes. Include your
                    business name, address, and TIN as well.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the completed paper form to the IRS at the{' '}
                    <Link
                      href="#1099-nec-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      address provided
                    </Link>{' '}
                    in the 1099 instructions.
                  </li>
                  <li>
                    Mail a copy of the 1099-NEC to the recipient by January
                    31st.{' '}
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important notes for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 0 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks before the deadline to ensure
                      they arrive on time.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      You must also send a copy of the 1099-NEC to the recipient
                      for their tax reporting.
                    </li>
                    <li id="1099-nec-mailing-address">
                      Paper filing requires printing, mailing, and tracking
                      forms for proof of compliance. Alternatively, you can file
                      1099-NEC forms electronically for faster processing and
                      delivery.
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-NEC - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The mailing address for Form 1099-NEC varies depending on your
                  business location. Below is a table summarizing the mailing
                  address for form 1099-nec:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-NEC to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service <br />
                            Austin Submission Processing Center
                            <br /> P.O. Box 149213
                            <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service Center
                            <br /> P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury Internal Revenue Service
                            Center
                            <br /> 1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 1099-NEC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can complete Form 1099-NEC filing in 3 simple steps. But,
                  before you begin the e-filing process, make sure you have all
                  the required information ready, so you can complete filing
                  swiftly.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Information required to file Form 1099-NEC:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Payer information, such as name, address, and TIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Recipient information, such as name, address, and TIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>Nonemployee compensation</li>
                  <li>Federal income tax withheld, if any</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have this information ready, you can follow the steps
                  below to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file form 1099-nec
                  </Link>{' '}
                  using TaxZerone.
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Step 1:</b> Fill out Form 1099-NEC
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Step 2:</b> Review & transmit the return
                  </li>
                  <li>
                    <b>Step 3:</b> Send the recipient copy
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 1099-NEC with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  When you choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-NEC
                  </Link>{' '}
                  with TaxZerone, you can:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Get IRS form validations to check your returns for
                      accuracy
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Bulk upload return records and complete a large volume of
                      returns in one go.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Email recipient copies by email</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Share recipient copies in a secure
                      portal—ZeroneVault—where recipients can access their
                      return copies at any time anywhere
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Complete filing at the best price in the industry—as low
                      as $0.59 per form
                    </ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Get these benefits by e-filing your
              <br />
              Form 1099-NEC with TaxZerone
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099necinstructions`}
              >
                E-file Form 1099-NEC
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099NECInstructions;
